if (!customElements.get('nearby-stores')) {
  class NearbyStores extends HTMLElement {
    constructor() {
      super();
      try {
        this.variants = JSON.parse(this.dataset.variants || '[]');
      } catch (e) {
        this.variants = [];
      }
      
      this.apiUrl = 'https://226u024qmd.execute-api.ap-south-1.amazonaws.com/prod/nearby-inventory-v1';
      this.productTitle = this.dataset.productTitle || '';
      this.productPrice = this.dataset.productPrice || '';
      this.productImage = this.dataset.productImage || '';
      
      this.onPincodeUpdate = this.onPincodeUpdate.bind(this);
      this.openPopup = this.openPopup.bind(this);
      this.closePopup = this.closePopup.bind(this);
      
      // Elements
      this.infoEl = this.querySelector('.nearby-stores-info');
      this.btnEl = this.querySelector('.nearby-stores-btn');
      
      // State
      this.stores = [];
      this.pincode = '';
      this.sortMode = 'availability'; // 'availability' | 'distance'
      
      if (this.btnEl) {
        this.btnEl.addEventListener('click', this.openPopup);
      }
      
      this.createPopup();
    }

    connectedCallback() {
      document.addEventListener('bonkers:pincode-updated', this.onPincodeUpdate);
      
      const separator = this.closest('.check-delivery-block__combined-box')?.querySelector('.check-delivery-separator');
      const storedPincode = localStorage.getItem('bonkers_delivery_pincode');
      
      if (storedPincode && /^\d{6}$/.test(storedPincode)) {
        if (this.infoEl) this.infoEl.parentElement.classList.remove('is-empty');
        if (separator) separator.style.display = 'flex';
        this.fetchData(storedPincode);
      } else {
        if (this.infoEl) {
          this.infoEl.parentElement.classList.add('has-custom-layout', 'is-empty');
          this.infoEl.innerHTML = '';
        }
        if (separator) separator.style.display = 'none';
        if (this.btnEl) this.btnEl.disabled = false;
      }
      

    }

    disconnectedCallback() {
      document.removeEventListener('bonkers:pincode-updated', this.onPincodeUpdate);
    }
    
    onPincodeUpdate(e) {
      const separator = this.closest('.check-delivery-block__combined-box')?.querySelector('.check-delivery-separator');
      if (e.detail && e.detail.pincode) {
        if (this.pincode === e.detail.pincode) return; // Prevent duplicate API calls on variant change
        if (this.infoEl) this.infoEl.parentElement.classList.remove('is-empty');
        if (separator) separator.style.display = 'flex';
        this.fetchData(e.detail.pincode);
      } else {
        // If pincode is cleared
        this.pincode = '';
        this.stores = [];
        if (this.infoEl) {
          this.infoEl.parentElement.classList.add('has-custom-layout', 'is-empty');
          this.infoEl.innerHTML = '';
        }
        if (separator) separator.style.display = 'none';
        if (this.btnEl) this.btnEl.disabled = false;
      }
    }
    
    cleanSku(sku) {
      return sku.replace(/^[ua]-/i, '');
    }

    async fetchData(pincode) {
      this.pincode = pincode;
      this.sortMode = 'availability'; // Reset to default on each new pincode
      if (!this.variants.length) return;
      
      // Make the element visible immediately so the loading dots are visible to the user
      this.style.display = 'block';

      // Show loading state
      if (this.infoEl) {
        this.infoEl.innerHTML = `
          <div class="check-delivery-block__checking cd--visible" style="margin-top: 10px;">
            <span class="check-delivery-block__checking-dot"></span>
            <span class="check-delivery-block__checking-dot"></span>
            <span class="check-delivery-block__checking-dot"></span>
            <span class="check-delivery-block__checking-text">Checking nearby stores…</span>
          </div>`;
      }
      
      // Make parallel API calls for each variant
      try {
        const promises = this.variants.map(variant => {
          return fetch(this.apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              "customerPincode": pincode,
              "facilitySearchRadius": 4000,
              "facilityOperationalType": "WAREHOUSE",
              "facilityStatus": "ENABLED",
              "itemType": {
                "skuCode": this.cleanSku(variant.sku),
                "quantity": 1
              }
            })
          }).then(res => res.json())
            .then(data => ({ variant, data, isDummy: false }));
        });

        // Dummy SKU call — fetches the COMPLETE list of all nearby stores regardless
        // of product inventory. Required so Distance mode shows every store, not just
        // those that carry this product's variants.
        const dummyPromise = fetch(this.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerPincode: pincode,
            facilitySearchRadius: 4000,
            facilityOperationalType: 'WAREHOUSE',
            facilityStatus: 'ENABLED',
            itemType: { skuCode: 'dummy_sku_for_store_availibility_on_pdp', quantity: 1 }
          })
        }).then(res => res.json())
          .then(data => ({ variant: null, data, isDummy: true }));

        promises.push(dummyPromise);

        const apiResults = await Promise.all(promises);
        this.processResults(apiResults);
        
      } catch (err) {
        console.error('Error fetching nearby inventory', err);
        if (this.infoEl) {
          this.infoEl.innerHTML = `<div class="nearby-stores-nearest">Unable to find nearby stores.</div>`;
        }
      }
    }
    
    processResults(results) {
      // Map of API facility code → store object
      const storeMap = new Map();

      results.forEach(result => {
        const { variant, data, isDummy } = result;
        if (!data.successful || !data.facilityWiseNearbyInventorySnapshotDTOList) return;

        data.facilityWiseNearbyInventorySnapshotDTOList.forEach(facility => {
          if (facility.operationalType !== 'STORE') return;

          // Blocklist: internal/test stores that must never surface on the PDP.
          const BLOCKED_DISPLAY_NAMES = ['bonkers event'];
          if (BLOCKED_DISPLAY_NAMES.includes(facility.displayName.trim().toLowerCase())) return;

          if (!storeMap.has(facility.code)) {
            // Build _info directly from backend-provided storeifyInfo — no frontend lookup needed.
            const si = facility.storeifyInfo;
            const webMatch = (si && si.web) ? si.web.match(/href="([^"]+)"/) : null;
            const webUrl = webMatch ? webMatch[1] : null;

            const info = si ? {
              name:          si.name    || '',
              address:       si.address || '',
              phone:         si.phone   || '',
              phoneHref:     si.phone   ? `tel:${si.phone.replace(/\s/g, '')}` : '',
              directionHref: webUrl 
                ? webUrl 
                : (si.lat && si.lng)
                  ? `https://maps.google.com/?q=${si.lat},${si.lng}`
                  : si.address ? `https://maps.google.com/?daddr=${encodeURIComponent(si.address)}` : '',
            } : null;

            storeMap.set(facility.code, {
              code:        facility.code,
              displayName: facility.displayName,
              pincode:     facility.facilityPincode,
              distance:    facility.distanceToCustomerPincode,
              storeifyId:  si ? si.id : null,
              _info:       info,
              variants:    this.variants.map(v => ({ title: v.title, available: false, inventory: 0 }))
            });
          }

          const store = storeMap.get(facility.code);
          // Only update inventory for real variant requests — never for the dummy SKU call.
          if (!isDummy && variant) {
            const inventory = facility.inventorySnapshot?.inventory || 0;
            const variantEntry = store.variants.find(v => v.title === variant.title);
            if (variantEntry) {
              variantEntry.inventory = inventory;
              variantEntry.available  = inventory > 0;
            }
          }
        });
      });

      // Deduplicate using storeifyInfo.id as the unique physical-store key.
      // Two API codes mapping to the same Storeify location get merged into one card.
      const resolved = new Map();
      for (const store of storeMap.values()) {
        const key = store.storeifyId != null ? `si_${store.storeifyId}` : `code_${store.code}`;
        if (resolved.has(key)) {
          const existing = resolved.get(key);
          store.variants.forEach((v, i) => {
            if (v.inventory > existing.variants[i].inventory) existing.variants[i] = { ...v };
          });
        } else {
          resolved.set(key, store);
        }
      }

      this.stores = [...resolved.values()].sort((a, b) => {
        const aHas = a.variants.some(v => v.available);
        const bHas = b.variants.some(v => v.available);
        if (aHas !== bHas) return aHas ? -1 : 1;
        return a.distance - b.distance;
      });

      this.updateWidgetUI();
      this.renderPopupStores();
    }
    
    updateWidgetUI() {
      if (!this.infoEl || !this.stores.length) {
        this.style.display = 'none';
        if (this.infoEl) {
          this.infoEl.parentElement.classList.add('has-custom-layout', 'is-empty');
          this.infoEl.innerHTML = '';
        }
        return;
      }
      
      this.style.display = 'block';
      
      const nearestStore = this.stores[0];
      const distanceDisplay = nearestStore.distance === 0 ? 'within 1km' : Math.round(nearestStore.distance) + 'km away';
      
      const contactInfo = nearestStore._info || { name: nearestStore.displayName, address: nearestStore.pincode, phone: '', phoneHref: '', directionHref: '' };
      const displayStoreName = contactInfo.name || nearestStore.displayName;
      
      let variantsHtml = '';
      nearestStore.variants.forEach((v) => {
        let clz = v.available ? '' : 'unavailable';
        variantsHtml += `<span class="nearby-size-box ${clz}">${v.title}</span>`;
      });

      this.infoEl.parentElement.classList.add('has-custom-layout');
      this.infoEl.innerHTML = `
        <div class="nearby-widget-populated">
           <div class="nearby-pop-header">
              <div class="nearby-store-icon">
                <svg width="25" height="25" viewBox="4.5 4 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.9426 8.674C10.9426 8.25979 10.6068 7.924 10.1926 7.924C9.77842 7.924 9.44263 8.25979 9.44263 8.674H10.9426ZM15.5566 8.674C15.5566 8.25979 15.2208 7.924 14.8066 7.924C14.3924 7.924 14.0566 8.25979 14.0566 8.674H15.5566ZM9.44274 8.66112C9.43563 9.07527 9.76559 9.41677 10.1797 9.42389C10.5939 9.43101 10.9354 9.10104 10.9425 8.68688L9.44274 8.66112ZM10.9525 8.10488C10.9596 7.69073 10.6297 7.34923 10.2155 7.34211C9.80136 7.33499 9.45986 7.66496 9.45274 8.07912L10.9525 8.10488ZM10.9201 8.85645C11.0209 8.45468 10.7769 8.04729 10.3751 7.94653C9.97331 7.84577 9.56593 8.08978 9.46516 8.49155L10.9201 8.85645ZM10.0356 9.3L10.7613 9.48948L10.7631 9.48245L10.0356 9.3ZM8.60872 10.8759L8.8691 11.5792H8.8691L8.60872 10.8759ZM6.927 9.99268C6.58662 9.75665 6.11934 9.84124 5.88331 10.1816C5.64728 10.522 5.73187 10.9893 6.07226 11.2253L6.927 9.99268ZM7.24959 10.6167C7.25384 10.2025 6.92151 9.86328 6.50732 9.85904C6.09312 9.85479 5.75392 10.1871 5.74967 10.6013L7.24959 10.6167ZM6.44463 15.976L7.19458 15.985L7.19459 15.9837L6.44463 15.976ZM7.02186 17.4058L6.48838 17.9329H6.48838L7.02186 17.4058ZM8.44463 18V17.25H8.44458L8.44463 18ZM10.4996 18.75C10.9138 18.75 11.2496 18.4142 11.2496 18C11.2496 17.5858 10.9138 17.25 10.4996 17.25V18.75ZM6.07039 11.224C6.41006 11.4611 6.87759 11.3779 7.11465 11.0382C7.35172 10.6986 7.26854 10.231 6.92887 9.99398L6.07039 11.224ZM5.57863 8.092L4.87557 7.83085C4.86723 7.85329 4.85997 7.87612 4.85382 7.89926L5.57863 8.092ZM6.41363 5.844L7.1167 6.10515C7.12393 6.08569 7.13034 6.06594 7.13593 6.04594L6.41363 5.844ZM7.52263 5V4.25L7.52046 4.25L7.52263 5ZM17.4756 5L17.4778 4.25H17.4756V5ZM18.5846 5.844L17.8623 6.04595C17.8679 6.0659 17.8743 6.08562 17.8815 6.10505L18.5846 5.844ZM19.4196 8.093L20.1445 7.90034C20.1383 7.87721 20.1311 7.85439 20.1227 7.83195L19.4196 8.093ZM18.0701 9.99422C17.7305 10.2315 17.6476 10.699 17.8848 11.0386C18.1221 11.3781 18.5897 11.461 18.9292 11.2238L18.0701 9.99422ZM9.74963 18C9.74963 18.4142 10.0854 18.75 10.4996 18.75C10.9138 18.75 11.2496 18.4142 11.2496 18H9.74963ZM13.7496 18C13.7496 18.4142 14.0854 18.75 14.4996 18.75C14.9138 18.75 15.2496 18.4142 15.2496 18H13.7496ZM10.4996 17.25C10.0854 17.25 9.74963 17.5858 9.74963 18C9.74963 18.4142 10.0854 18.75 10.4996 18.75V17.25ZM14.4996 18.75C14.9138 18.75 15.2496 18.4142 15.2496 18C15.2496 17.5858 14.9138 17.25 14.4996 17.25V18.75ZM14.0567 8.68302C14.0617 9.0972 14.4015 9.42893 14.8157 9.42395C15.2298 9.41896 15.5616 9.07916 15.5566 8.66498L14.0567 8.68302ZM15.5496 8.08298C15.5446 7.6688 15.2048 7.33707 14.7906 7.34205C14.3764 7.34704 14.0447 7.68684 14.0497 8.10102L15.5496 8.08298ZM15.5369 8.49073C15.4357 8.08907 15.028 7.84552 14.6264 7.94674C14.2247 8.04796 13.9811 8.45562 14.0824 8.85727L15.5369 8.49073ZM14.9666 9.297L14.2393 9.48028L14.2409 9.48622L14.9666 9.297ZM16.3915 10.8729L16.1304 11.5759L16.3915 10.8729ZM18.926 11.226C19.2667 10.9906 19.3521 10.5235 19.1167 10.1827C18.8812 9.84189 18.4141 9.7565 18.0733 9.99195L18.926 11.226ZM19.2496 10.6017C19.2456 10.1875 18.9066 9.85502 18.4924 9.85904C18.0782 9.86305 17.7457 10.2021 17.7497 10.6163L19.2496 10.6017ZM18.5516 15.976L17.8017 15.9833L17.8017 15.985L18.5516 15.976ZM17.9744 17.4058L17.4409 16.8786L17.9744 17.4058ZM16.5516 18L16.5517 17.25H16.5516V18ZM14.4996 17.25C14.0854 17.25 13.7496 17.5858 13.7496 18C13.7496 18.4142 14.0854 18.75 14.4996 18.75V17.25ZM9.44263 8.674C9.44263 10.3623 10.8113 11.731 12.4996 11.731V10.231C11.6397 10.231 10.9426 9.53391 10.9426 8.674H9.44263ZM12.4996 11.731C14.188 11.731 15.5566 10.3623 15.5566 8.674H14.0566C14.0566 9.53391 13.3595 10.231 12.4996 10.231V11.731ZM10.9425 8.68688L10.9525 8.10488L9.45274 8.07912L9.44274 8.66112L10.9425 8.68688ZM9.46516 8.49155L9.30816 9.11755L10.7631 9.48245L10.9201 8.85645L9.46516 8.49155ZM9.30996 9.11053C9.18207 9.60032 8.82307 9.9968 8.34835 10.1725L8.8691 11.5792C9.80324 11.2334 10.5097 10.4533 10.7613 9.48947L9.30996 9.11053ZM8.34835 10.1725C7.87362 10.3483 7.34299 10.2811 6.927 9.99268L6.07226 11.2253C6.89081 11.7929 7.93496 11.9251 8.8691 11.5792L8.34835 10.1725ZM5.74967 10.6013L5.69467 15.9683L7.19459 15.9837L7.24959 10.6167L5.74967 10.6013ZM5.69469 15.967C5.68586 16.702 5.9717 17.41 6.48838 17.9329L7.55535 16.8786C7.32049 16.6409 7.19057 16.3191 7.19458 15.985L5.69469 15.967ZM6.48838 17.9329C7.00507 18.4558 7.70959 18.7501 8.44469 18.75L8.44458 17.25C8.11044 17.25 7.7902 17.1163 7.55535 16.8786L6.48838 17.9329ZM8.44463 18.75H10.4996V17.25H8.44463V18.75ZM6.92887 9.99398C6.38194 9.61226 6.13204 8.92931 6.30344 8.28474L4.85382 7.89926C4.52041 9.15305 5.00651 10.4815 6.07039 11.224L6.92887 9.99398ZM6.2817 8.35315L7.1167 6.10515L5.71057 5.58285L4.87557 7.83085L6.2817 8.35315ZM7.13593 6.04594C7.18473 5.8714 7.34356 5.75052 7.5248 5.75L7.52046 4.25C6.66795 4.25247 5.92088 4.82103 5.69133 5.64206L7.13593 6.04594ZM7.52263 5.75H17.4756V4.25H7.52263V5.75ZM17.4735 5.75C17.6547 5.75052 17.8135 5.8714 17.8623 6.04595L19.3069 5.64205C19.0774 4.82103 18.3303 4.25247 17.4778 4.25L17.4735 5.75ZM17.8815 6.10505L18.7165 8.35405L20.1227 7.83195L19.2877 5.58295L17.8815 6.10505ZM18.6948 8.28566C18.866 8.92987 18.6164 9.61242 18.0701 9.99422L18.9292 11.2238C19.9921 10.4811 20.4775 9.15344 20.1445 7.90034L18.6948 8.28566ZM11.2496 18V15H9.74963V18H11.2496ZM11.2496 15C11.2496 14.8619 11.3616 14.75 11.4996 14.75V13.25C10.5331 13.25 9.74963 14.0335 9.74963 15H11.2496ZM11.4996 14.75H13.4996V13.25H11.4996V14.75ZM13.4996 14.75C13.6377 14.75 13.7496 14.8619 13.7496 15H15.2496C15.2496 14.0335 14.4661 13.25 13.4996 13.25V14.75ZM13.7496 15V18H15.2496V15H13.7496ZM10.4996 18.75H14.4996V17.25H10.4996V18.75ZM15.5566 8.66498L15.5496 8.08298L14.0497 8.10102L14.0567 8.68302L15.5566 8.66498ZM14.0824 8.85727L14.2394 9.48027L15.6939 9.11373L15.5369 8.49073L14.0824 8.85727ZM14.2409 9.48622C14.492 10.4494 15.1973 11.2294 16.1304 11.5759L16.6526 10.1698C16.1784 9.99367 15.82 9.59726 15.6924 9.10778L14.2409 9.48622ZM16.1304 11.5759C17.0635 11.9225 18.107 11.7919 18.926 11.226L18.0733 9.99195C17.6571 10.2795 17.1268 10.3459 16.6526 10.1698L16.1304 11.5759ZM17.7497 10.6163L17.8017 15.9833L19.3016 15.9687L19.2496 10.6017L17.7497 10.6163ZM17.8017 15.985C17.8057 16.3191 17.6758 16.6409 17.4409 16.8786L18.5079 17.9329C19.0246 17.41 19.3104 16.702 19.3016 15.967L17.8017 15.985ZM17.4409 16.8786C17.2061 17.1163 16.8858 17.25 16.5517 17.25L16.5516 18.75C17.2867 18.7501 17.9912 18.4558 18.5079 17.9329L17.4409 16.8786ZM16.5516 17.25H14.4996V18.75H16.5516V17.25Z" fill="#111111"/>
                </svg>
              </div>
              <div class="nearby-pop-title-wrap" style="flex: 1; min-width: 0;">
                 <div class="nearby-pop-title-row" style="justify-content: space-between; align-items: flex-start; flex-wrap: wrap;">
                    <div style="display: flex; flex-direction: column; gap: 4px;">
                       <div style="display: flex; align-items: flex-start; gap: 8px;">
                          <span class="nearby-pop-title" style="white-space: normal; line-height: 1.2; max-width: 100%; color: #1a1a1a;">${displayStoreName}</span>
                          <span class="nearby-badge" style="flex-shrink:0;">Nearest</span>
                       </div>
                       <span class="nearby-distance" style="margin:0; font-size: 13px; color: #666;">${distanceDisplay}</span>
                    </div>
                    <div style="display: flex; align-items: flex-start; gap: 10px; margin-top:2px;">
                       ${contactInfo.phoneHref ? `<a href="${contactInfo.phoneHref}" class="nearby-pop-phone-icon" aria-label="Call Store">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                       </a>` : ''}
                       ${contactInfo.directionHref ? `<a href="${contactInfo.directionHref}" target="_blank" class="nearby-pop-phone-icon" aria-label="Get Directions">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                             <g stroke-linecap="round" stroke-linejoin="round"/>
                             <path d="M18.9762 5.5914L14.6089 18.6932C14.4726 19.1023 13.8939 19.1023 13.7575 18.6932L11.7868 12.7808C11.6974 12.5129 11.4871 12.3026 11.2192 12.2132L5.30683 10.2425C4.89772 10.1061 4.89772 9.52743 5.30683 9.39106L18.4086 5.0238C18.7594 4.90687 19.0931 5.24061 18.9762 5.5914Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                       </a>` : ''}
                    </div>
                 </div>
              </div>
           </div>
           
           <div class="nearby-pop-sizes-container" ${this.variants.length === 1 ? 'style="display:none;"' : ''}>
              <div class="nearby-pop-sizes-label">Available Sizes:</div>
              <div class="nearby-pop-sizes">${variantsHtml}</div>
           </div>
           
           <div class="nearby-pop-actions" style="justify-content: flex-end; margin-top: 15px;">
              <button class="nearby-pop-view-all" style="flex: auto; text-align: center;">View All Stores ></button>
           </div>
        </div>
      `;
      // bind openPopup to the new view all button
      this.infoEl.querySelector('.nearby-pop-view-all').addEventListener('click', this.openPopup);
    }
    

    
    createPopup() {
      // Check if modal container already exists
      if (document.getElementById('nearby-stores-modal')) return;
      
      const backdrop = document.createElement('div');
      backdrop.id = 'nearby-stores-modal';
      backdrop.className = 'nearby-modal-backdrop';
      
      const template = `
        <div class="nearby-modal">
          <button class="nearby-modal-close" aria-label="Close">&times;</button>
          <div class="nearby-modal-body">
            <div class="nearby-modal-header-product">
              <img src="${this.productImage}" alt="">
              <div class="nearby-modal-header-text">
                <div class="nearby-modal-header-title">${this.productTitle}</div>
                <div class="nearby-modal-header-price">${this.productPrice}</div>
              </div>
            </div>
            
            <div class="nearby-modal-search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input type="text" placeholder="Search stores by name or location..." id="nearby-store-search">
            </div>
            
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-top: 4px;">
              <span style="font-size: 14px; font-weight: 700; color: #111;">Nearby Stores</span>
              <div class="sort-wrap">
                <button class="sort-btn" id="sortBtn" type="button">
                  <!-- sort-arrows icon -->
                  <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8l4-4 4 4"/><path d="M7 4v14"/><path d="M21 16l-4 4-4-4"/><path d="M17 20V6"/></svg>
                  <span id="sortLabel">${this.sortMode === 'availability' ? 'Available first' : 'Closest first'}</span>
                  <!-- chevron-down icon -->
                  <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                </button>
          
                <div class="sort-menu" id="sortMenu">
                  <button class="sort-option ${this.sortMode === 'availability' ? 'selected' : ''}" type="button" data-value="availability">
                    <span style="display:flex; align-items:center; gap:8px;">
                      <svg class="base-icon" viewBox="0 0 24 24" fill="none" stroke="#9B9A95" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                      Available first
                    </span>
                    <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                  </button>
                  <button class="sort-option ${this.sortMode === 'distance' ? 'selected' : ''}" type="button" data-value="distance">
                    <span style="display:flex; align-items:center; gap:8px;">
                      <svg class="base-icon" viewBox="0 0 24 24" fill="none" stroke="#9B9A95" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-7-6.5-7-11a7 7 0 1 1 14 0c0 4.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>
                      Closest first
                    </span>
                    <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                  </button>
                </div>
              </div>
            </div>
            
            <div class="nearby-modal-list" id="nearby-stores-list">
              <!-- Stores dynamically rendered here -->
            </div>
          </div>
        </div>
      `;
      
      backdrop.innerHTML = template;
      document.body.appendChild(backdrop);
      
      // Events
      backdrop.querySelector('.nearby-modal-close').addEventListener('click', () => {
        this.closePopup();
      });
      
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this.closePopup();
      });
      
      backdrop.querySelector('#nearby-store-search').addEventListener('input', (e) => {
        const val = e.target.value.toLowerCase().trim();
        // If there are no stores and they type a valid pincode, instantly fetch
        if (this.stores.length === 0 && /^\d{6}$/.test(val)) {
           // We dispatch a global update so the rest of the site (like the Check Delivery widget) syncs up too!
           document.dispatchEvent(new CustomEvent('bonkers:pincode-updated', { detail: { pincode: val } }));
        } else {
           this.filterStoresUi(val, false);
        }
      });
      const sortBtn = backdrop.querySelector('#sortBtn');
      const sortMenu = backdrop.querySelector('#sortMenu');
      const sortLabel = backdrop.querySelector('#sortLabel');
      const sortOptions = backdrop.querySelectorAll('.sort-option');

      if (sortBtn && sortMenu) {
        sortBtn.addEventListener('click', () => {
          const isOpen = sortMenu.classList.contains('open');
          sortMenu.classList.toggle('open', !isOpen);
          sortBtn.classList.toggle('open', !isOpen);
        });

        sortOptions.forEach((opt) => {
          opt.addEventListener('click', () => {
            sortOptions.forEach((o) => o.classList.remove('selected'));
            opt.classList.add('selected');
            sortLabel.textContent = opt.querySelector('span').textContent;
            sortMenu.classList.remove('open');
            sortBtn.classList.remove('open');

            this.sortMode = opt.dataset.value;
            this.renderPopupStores();
          });
        });

        // Close dropdown when clicking outside
        backdrop.addEventListener('click', (e) => {
          if (!sortBtn.contains(e.target) && !sortMenu.contains(e.target)) {
            sortMenu.classList.remove('open');
            sortBtn.classList.remove('open');
          }
        });
      }
    }

    renderPopupStores() {
      const modal = document.getElementById('nearby-stores-modal');
      if (!modal) return;

      // Compute the display list based on sort mode:
      // - 'availability': only stores with >=1 available variant, sorted by distance (already ordered)
      // - 'distance': all stores sorted strictly by distance low-to-high
      const displayStores = this.sortMode === 'availability'
        ? this.stores.filter(s => s.variants.some(v => v.available))
        : [...this.stores].sort((a, b) => a.distance - b.distance);

      const listEl = modal.querySelector('#nearby-stores-list');
      if (!listEl) return;

      listEl.innerHTML = '';

      if (displayStores.length === 0) {
        const msg = this.sortMode === 'availability'
          ? `No stores have this product in stock near <strong>${this.pincode}</strong>.`
          : `Sorry, no stores found near <strong>${this.pincode}</strong>.`;
        listEl.innerHTML = `<div style="padding:40px 20px; text-align:center; color:#666; font-size:14px;">${msg}</div>`;
        return;
      }
      
      displayStores.forEach((store, index) => {
        // Use Storeify info resolved by the backend and cached on store._info
        const contactInfo = store._info || {
          name:          store.displayName,
          address:       store.pincode,
          phone:         '',
          phoneHref:     '',
          directionHref: `https://maps.google.com/?q=${encodeURIComponent(store.displayName)}`
        };
        const displayStoreName = contactInfo.name || store.displayName;
        
        const card = document.createElement('div');
        card.className = 'nearby-store-card';
        // Add searchable data attribute
        card.dataset.search = `${displayStoreName.toLowerCase()} ${contactInfo.address.toLowerCase()} ${store.pincode}`;
        
        let hasAvailableSize = false;
        
        // Variants formatting
        let variantsHtml = '<div class="nearby-pop-sizes">';
        store.variants.forEach((v) => {
          if (v.available) hasAvailableSize = true;
          let clz = v.available ? '' : 'unavailable';
          variantsHtml += `<span class="nearby-size-box ${clz}">${v.title}</span>`;
        });
        variantsHtml += '</div>';
        
        card.dataset.hasAvailable = hasAvailableSize ? 'true' : 'false';
        
        let distanceText = store.distance === 0 ? 'within 1km' : Math.round(store.distance) + 'km away';
        let nearestBadge = index === 0 ? `<span class="nearby-badge" style="margin:0; flex-shrink:0;">Nearest</span>` : '';
        
        // Conditionally render icons — only if the relevant data is available
        let directionsIcon = contactInfo.directionHref ? `<a href="${contactInfo.directionHref}" target="_blank" aria-label="Get Directions">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M18.9762 5.5914L14.6089 18.6932C14.4726 19.1023 13.8939 19.1023 13.7575 18.6932L11.7868 12.7808C11.6974 12.5129 11.4871 12.3026 11.2192 12.2132L5.30683 10.2425C4.89772 10.1061 4.89772 9.52743 5.30683 9.39106L18.4086 5.0238C18.7594 4.90687 19.0931 5.24061 18.9762 5.5914Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>` : '';

        let phoneIcon = contactInfo.phoneHref ? `<a href="${contactInfo.phoneHref}" aria-label="Call Store">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
        </a>` : '';
        
        card.innerHTML = `
          <div class="nearby-store-header">
            <div style="display:flex; align-items:flex-start; gap:2px; flex-direction: column-reverse;">
              <div class="nearby-store-name" style="font-size:15px; font-weight:700;">${displayStoreName}</div>
              ${nearestBadge}
            </div>
            <div class="nearby-store-icons" style="display:flex; gap:12px; align-items:center;">
              ${phoneIcon}
              ${directionsIcon}
            </div>
          </div>
          
          <div class="nearby-pop-address" style="margin-bottom:4px;">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
            ${contactInfo.address}
          </div>
          <div class="nearby-distance" style="font-size:13px;">${distanceText}</div>
          
          <hr style="border:none; border-top:1px solid #e2e8f0; margin:15px 0;" ${this.variants.length === 1 ? 'hidden' : ''}>
          
          <div class="nearby-pop-sizes-container" ${this.variants.length === 1 ? 'style="display:none;"' : ''}>
            <div class="nearby-pop-sizes-label" style="font-size:12px; font-weight:700; margin-bottom:8px;">Available Sizes:</div>
            ${variantsHtml}
          </div>
        `;
        
        listEl.appendChild(card);
      });
    }
    
    filterStoresUi(searchTerm, mustHaveSize) {
      const modal = document.getElementById('nearby-stores-modal');
      if (!modal) return;
      const listEl = modal.querySelector('#nearby-stores-list');
      const cards = listEl.querySelectorAll('.nearby-store-card');
      
      cards.forEach(card => {
        const text = card.dataset.search;
        const hasSize = card.dataset.hasAvailable === 'true';
        let show = true;
        
        if (searchTerm && !text.includes(searchTerm)) show = false;
        if (mustHaveSize && !hasSize) show = false;
        
        card.style.display = show ? 'block' : 'none';
      });
    }

    openPopup() {
      // Best UX practice: If there is no pincode loaded yet, focus the primary Check Delivery input
      if (!this.pincode) {
        const deliveryInput = document.querySelector('.check-delivery-block__input');
        if (deliveryInput) {
          deliveryInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          deliveryInput.focus({ preventScroll: true });
          return;
        }
      }

      const modal = document.getElementById('nearby-stores-modal');
      if (modal) {
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    }
    
    closePopup() {
      const modal = document.getElementById('nearby-stores-modal');
      if (modal) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }
  }

  customElements.define('nearby-stores', NearbyStores);
}

